import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { resolveGroupApplicationIds } from './deliveryGroupModel';
import {
  buildExecutionOwnershipIndex,
  groupOwnsApplication,
  resolveExecutionOwnerGroupUid
} from './deliveryGroupExecutionOwnershipResolver';
import {
  validateMultiEdgeAllocations,
  type MultiEdgeValidationIssue
} from './allocationEdgeDraft';
import {
  determineExecutionRelations,
  type ExecutionRelation
} from './executionAuthorityResolver';
import type {
  AllocationEdgeDraft,
  FromStopDraftByUid,
  QuantityDraftByCargo,
  ToStopDraftByUid
} from './executionDraftTypes';
import type { ExecutionTopologyPreview } from './executionTopologyHelpers';

export type ExecutionGraphIssue = {
  code:
    | 'MISSING_LOADING_STOP'
    | 'MISSING_UNLOADING_STOP'
    | 'CROSS_GROUP'
    | 'QTY_EXCEEDS_AVAILABLE'
    | 'UNKNOWN_STOP';
  message: string;
  cargoId?: number;
};

function mapMultiEdgeIssue(issue: MultiEdgeValidationIssue): ExecutionGraphIssue {
  const code =
    issue.code === 'MISSING_UNLOADING_STOP'
      ? 'MISSING_UNLOADING_STOP'
      : issue.code === 'QTY_EXCEEDS_AVAILABLE' || issue.code === 'QTY_EXCEEDS_CAP'
        ? 'QTY_EXCEEDS_AVAILABLE'
        : issue.code === 'MISSING_LOADING_STOP'
          ? 'MISSING_LOADING_STOP'
          : 'QTY_EXCEEDS_AVAILABLE';
  return { code, message: issue.message, cargoId: issue.cargoId };
}

export function validateExecutionGraph(params: {
  groups: DeliveryGroupDraft[];
  selectedIds: number[];
  fromStopDraft: FromStopDraftByUid;
  toStopDraft: ToStopDraftByUid;
  quantityDraft: QuantityDraftByCargo;
  allocationEdges?: AllocationEdgeDraft[];
  topology?: ExecutionTopologyPreview | null;
  cargoApplicationById?: Record<number, number | undefined>;
  cargoAvailableById?: Record<number, number | undefined>;
  cargoCapById?: Record<number, number | undefined>;
}): ExecutionGraphIssue[] {
  const issues: ExecutionGraphIssue[] = [];
  const positionByStopUid =
    params.topology?.positionByStopUid ?? buildPositionIndexFromGroups(params.groups);

  if (params.allocationEdges && params.allocationEdges.length > 0) {
    const capById: Record<number, number | undefined> = { ...params.cargoCapById };
    for (const cargoId of params.selectedIds) {
      if (capById[cargoId] == null) {
        const avail = params.cargoAvailableById?.[cargoId];
        if (avail != null) capById[cargoId] = avail;
      }
    }
    const edgeIssues = validateMultiEdgeAllocations({
      edges: params.allocationEdges,
      cargoCapById: capById,
      cargoAvailableById: params.cargoAvailableById
    });
    issues.push(...edgeIssues.map(mapMultiEdgeIssue));

    const relations = determineExecutionRelations({
      selectedIds: params.selectedIds,
      fromStopDraft: params.fromStopDraft,
      toStopDraft: params.toStopDraft,
      quantityDraft: params.quantityDraft,
      positionByStopUid,
      allocationEdges: params.allocationEdges
    });

    validateSharedUnloadAllowed(relations, params.groups, issues);
    validateEdgeOwnership(relations, params, issues);
    return issues;
  }

  const relations = determineExecutionRelations({
    selectedIds: params.selectedIds,
    fromStopDraft: params.fromStopDraft,
    toStopDraft: params.toStopDraft,
    quantityDraft: params.quantityDraft,
    positionByStopUid
  });

  const ownershipIndex = buildExecutionOwnershipIndex(params.groups);

  for (const cargoId of params.selectedIds) {
    const qty = Number(params.quantityDraft[cargoId] ?? 0);
    if (qty <= 0) continue;

    const fromUid = params.fromStopDraft[cargoId];
    const toUid = params.toStopDraft[cargoId];

    if (!fromUid) {
      issues.push({
        code: 'MISSING_LOADING_STOP',
        cargoId,
        message: `Груз #${cargoId}: не указана точка погрузки`
      });
      continue;
    }
    if (!toUid) {
      issues.push({
        code: 'MISSING_UNLOADING_STOP',
        cargoId,
        message: `Груз #${cargoId}: не указана точка выгрузки`
      });
      continue;
    }

    if (!ownershipIndex.has(fromUid) || !ownershipIndex.has(toUid)) {
      issues.push({
        code: 'UNKNOWN_STOP',
        cargoId,
        message: `Груз #${cargoId}: точка не найдена в execution graph`
      });
      continue;
    }

    const fromGroupUid = resolveExecutionOwnerGroupUid(fromUid, params.groups);
    const toGroupUid = resolveExecutionOwnerGroupUid(toUid, params.groups);
    const appId = params.cargoApplicationById?.[cargoId];

    if (fromGroupUid && appId != null) {
      const ownerGroup = params.groups.find((g) => g.uid === fromGroupUid);
      if (ownerGroup && !groupOwnsApplication(ownerGroup, appId)) {
        issues.push({
          code: 'CROSS_GROUP',
          cargoId,
          message: `Груз #${cargoId} (заявка #${appId}) не принадлежит execution group точки погрузки`
        });
      }
    }

    if (fromGroupUid && toGroupUid && fromGroupUid !== toGroupUid) {
      if (appId != null) {
        const fromGroup = params.groups.find((g) => g.uid === fromGroupUid);
        if (fromGroup && !resolveGroupApplicationIds(fromGroup).has(Number(appId))) {
          issues.push({
            code: 'CROSS_GROUP',
            cargoId,
            message: `Груз #${cargoId}: cross-group execution binding запрещён`
          });
        }
      }
    }

    const available = params.cargoAvailableById?.[cargoId];
    if (available != null && qty > available) {
      issues.push({
        code: 'QTY_EXCEEDS_AVAILABLE',
        cargoId,
        message: `Груз #${cargoId}: количество ${qty} превышает доступное ${available}`
      });
    }
  }

  validateSharedUnloadAllowed(relations, params.groups, issues);

  return issues;
}

function validateEdgeOwnership(
  relations: ExecutionRelation[],
  params: {
    groups: DeliveryGroupDraft[];
    cargoApplicationById?: Record<number, number | undefined>;
  },
  issues: ExecutionGraphIssue[]
): void {
  const ownershipIndex = buildExecutionOwnershipIndex(params.groups);

  for (const rel of relations) {
    const cargoId = rel.cargoId;
    if (!ownershipIndex.has(rel.fromStopUid) || !ownershipIndex.has(rel.toStopUid)) {
      issues.push({
        code: 'UNKNOWN_STOP',
        cargoId,
        message: `Груз #${cargoId}: точка не найдена в execution graph`
      });
      continue;
    }

    const fromGroupUid = resolveExecutionOwnerGroupUid(rel.fromStopUid, params.groups);
    const toGroupUid = resolveExecutionOwnerGroupUid(rel.toStopUid, params.groups);
    const appId = params.cargoApplicationById?.[cargoId];

    if (fromGroupUid && appId != null) {
      const ownerGroup = params.groups.find((g) => g.uid === fromGroupUid);
      if (ownerGroup && !groupOwnsApplication(ownerGroup, appId)) {
        issues.push({
          code: 'CROSS_GROUP',
          cargoId,
          message: `Груз #${cargoId} (заявка #${appId}) не принадлежит execution group точки погрузки`
        });
      }
    }

    if (fromGroupUid && toGroupUid && fromGroupUid !== toGroupUid) {
      if (appId != null) {
        const fromGroup = params.groups.find((g) => g.uid === fromGroupUid);
        if (fromGroup && !resolveGroupApplicationIds(fromGroup).has(Number(appId))) {
          issues.push({
            code: 'CROSS_GROUP',
            cargoId,
            message: `Груз #${cargoId}: cross-group execution binding запрещён`
          });
        }
      }
    }
  }
}

function validateSharedUnloadAllowed(
  relations: ExecutionRelation[],
  groups: DeliveryGroupDraft[],
  issues: ExecutionGraphIssue[]
): void {
  const unloadToLoadings = new Map<string, Set<string>>();
  for (const rel of relations) {
    if (!unloadToLoadings.has(rel.toStopUid)) unloadToLoadings.set(rel.toStopUid, new Set());
    unloadToLoadings.get(rel.toStopUid)!.add(rel.fromStopUid);
  }

  for (const [unloadUid, loadingUids] of Array.from(unloadToLoadings.entries())) {
    if (loadingUids.size <= 1) continue;
    const owner = resolveExecutionOwnerGroupUid(unloadUid, groups);
    if (!owner) continue;
    for (const loadUid of Array.from(loadingUids)) {
      const loadOwner = resolveExecutionOwnerGroupUid(loadUid, groups);
      if (loadOwner && loadOwner !== owner) {
        issues.push({
          code: 'CROSS_GROUP',
          message: `Shared unload ${unloadUid}: loading stops принадлежат разным execution groups`
        });
        break;
      }
    }
  }
}

function buildPositionIndexFromGroups(groups: DeliveryGroupDraft[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const group of groups) {
    for (const s of [...group.loadingStops, ...group.unloadingStops]) {
      if (s.position != null) map.set(s.uid, s.position);
    }
    if (group.isDefault && group.loadingStops[0]) {
      map.set(group.loadingStops[0].uid, group.loadingPosition ?? 0);
    }
  }
  return map;
}
